import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import type { Table } from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ServerSideDataTablePaginationProps<TData> {
  table: Table<TData>;
  disablePagination?: boolean;
  totalItems?: number;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function ServerSideDataTablePagination<TData>({
  table,
  disablePagination = false,
  totalItems,
  currentPage,
  totalPages,
  pageSize: _pageSize,
  onPageChange,
  onPageSizeChange,
}: ServerSideDataTablePaginationProps<TData>) {
  const [stablePageIndex, setStablePageIndex] = useState(() =>
    currentPage !== undefined
      ? currentPage - 1
      : table.getState().pagination.pageIndex,
  );
  const [stablePageSize, setStablePageSize] = useState(
    () => _pageSize ?? table.getState().pagination.pageSize,
  );
  const [stablePageCount, setStablePageCount] = useState(
    () => totalPages || table.getPageCount(),
  );
  const [stableTotalRowsCount, setStableTotalRowsCount] = useState(
    () => totalItems || table.getFilteredRowModel().rows.length,
  );

  useEffect(() => {
    if (currentPage !== undefined) {
      setStablePageIndex(currentPage - 1);
    }
  }, [currentPage]);

  useEffect(() => {
    if (_pageSize !== undefined) {
      setStablePageSize(_pageSize);
    }
  }, [_pageSize]);

  useEffect(() => {
    if (totalPages !== undefined && totalPages > 0) {
      setStablePageCount(totalPages);
    }
  }, [totalPages]);

  useEffect(() => {
    if (totalItems !== undefined) {
      setStableTotalRowsCount(totalItems);
    }
  }, [totalItems]);

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    table.setPageSize(newPageSize);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  const handlePageChange = (newPageIndex: number) => {
    table.setPageIndex(newPageIndex);
    if (onPageChange) {
      onPageChange(newPageIndex + 1);
    }
  };

  const pageIndex = stablePageIndex;
  const pageSize = stablePageSize;
  const pageCount = stablePageCount;
  const selectedRowsCount = table.getFilteredSelectedRowModel().rows.length;
  const totalRowsCount = stableTotalRowsCount;

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {selectedRowsCount} von {totalRowsCount} Zeile(n) ausgewählt.
      </div>
      {!disablePagination && (
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Zeilen pro Seite</p>
            <Select
              value={`${pageSize}`}
              onValueChange={handlePageSizeChange}
              defaultValue={`${pageSize}`}
            >
              <SelectTrigger className="h-8 w-auto">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[50, 100, 200].map((pageSizeOption) => (
                  <SelectItem key={pageSizeOption} value={`${pageSizeOption}`}>
                    {pageSizeOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-auto items-center justify-center text-sm font-medium">
            Seite {pageIndex + 1} von {pageCount}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => handlePageChange(0)}
              disabled={!canPreviousPage}
            >
              <span className="sr-only">Go to first page</span>
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => handlePageChange(pageIndex - 1)}
              disabled={!canPreviousPage}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => handlePageChange(pageIndex + 1)}
              disabled={!canNextPage}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => handlePageChange(pageCount - 1)}
              disabled={!canNextPage}
            >
              <span className="sr-only">Go to last page</span>
              <DoubleArrowRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
